import { Button } from '@/components/Button/Button';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Ionicons } from '@expo/vector-icons';
import { XStack } from 'tamagui';

/**
 * Props for the BottomButtonBar component.
 * Defines the interface for a reusable button bar typically displayed at the bottom of forms.
 */
export interface BottomButtonBarProps {
  /**
   * Whether to show the back button.
   * Typically hidden on the first step of a form.
   */
  showBackButton?: boolean;
  /**
   * Callback for the back button press.
   */
  onBack?: () => void;
  /**
   * Whether to show the save as draft button.
   */
  showSaveAsDraft?: boolean;
  /**
   * Callback for the save as draft button press.
   */
  onSaveAsDraft?: () => void;
  /**
   * Label for the primary action button (e.g., "Next" or "Save").
   */
  primaryButtonLabel: string;
  /**
   * Callback for the primary action button press.
   */
  onPrimaryAction: () => void;
  /**
   * Whether the primary button is in a loading state.
   */
  primaryButtonLoading?: boolean;
  /**
   * Whether the primary button is disabled.
   */
  primaryButtonDisabled?: boolean;
  /**
   * Optional custom content to render on the left side (e.g., delete button).
   */
  leftAccessory?: React.ReactNode;
  /**
   * Optional custom content to render before the draft/primary actions.
   */
  rightLeadingAction?: React.ReactNode;
}

/**
 * Generic BottomButtonBar component for multi-step forms.
 * Displays action buttons at the bottom of forms with responsive behavior.
 *
 * Features:
 * - Back button (optional, shows icon on mobile, text on desktop)
 * - Save as draft button (optional)
 * - Primary action button (Next/Save)
 * - Responsive layout adapting to screen size
 *
 * @example
 * <BottomButtonBar
 *   showBackButton
 *   onBack={handleBack}
 *   showSaveAsDraft
 *   onSaveAsDraft={handleSaveDraft}
 *   primaryButtonLabel="Next"
 *   onPrimaryAction={handleNext}
 * />
 */
export const BottomButtonBar = ({
  showBackButton = false,
  onBack,
  showSaveAsDraft = true,
  onSaveAsDraft,
  primaryButtonLabel,
  onPrimaryAction,
  primaryButtonLoading = false,
  primaryButtonDisabled = false,
  leftAccessory,
  rightLeadingAction,
}: BottomButtonBarProps): React.ReactElement => {
  const { t } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();

  return (
    <XStack
      gap="$3"
      paddingTop="$true"
      borderTopWidth={1}
      borderTopColor="$borderColor"
      justifyContent="space-between"
      alignItems="center"
    >
      {/* Left side: Back button or custom accessory */}
      {showBackButton && onBack ? (
        <Button
          title={isSmallScreen ? undefined : t('buttons.back')}
          icon={
            isSmallScreen ? (
              <Ionicons name="chevron-back" size={20} />
            ) : undefined
          }
          variant="outlined"
          onPress={onBack}
        />
      ) : (
        leftAccessory
      )}

      {/* Right side: Save as draft and Primary action */}
      <XStack gap="$3" flex={1} justifyContent="flex-end">
        {rightLeadingAction}
        {showSaveAsDraft && onSaveAsDraft && (
          <Button
            title={t('reportCreation.buttons.saveAsDraft')}
            variant="outlined"
            onPress={onSaveAsDraft}
            flex={isSmallScreen ? 1 : 0}
            maxWidth="50%"
          />
        )}
        <Button
          title={primaryButtonLabel}
          variant="primary"
          onPress={onPrimaryAction}
          loading={primaryButtonLoading}
          disabled={primaryButtonDisabled}
          flex={isSmallScreen ? 1 : 0}
          maxWidth="50%"
        />
      </XStack>
    </XStack>
  );
};
